import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MapPin } from "lucide-react";
import { fetchAddressSuggestions } from "@/lib/location-autocomplete";

type Picked = {
  address: string;
  city?: string;
  state?: string;
  zip?: string;
};

export function AddressAutocomplete({
  label = "Address *",
  address,
  onPick,
}: {
  label?: string;
  address: string;
  onPick: (v: Picked) => void;
}) {
  const [suggestions, setSuggestions] = useState<{ label: string; data: Picked }[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onChange = (val: string) => {
    onPick({ address: val });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await fetchAddressSuggestions(val);
        setSuggestions(items.map(({ label, ...data }) => ({ label, data })));
        setOpen(items.length > 0);
      } catch {}
      setLoading(false);
    }, 300);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Label className="text-xs">{label}</Label>
      <div className="relative mt-1">
        <Input
          value={address}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder="Start typing the street address…"
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-72 overflow-auto border border-border bg-popover shadow-md">
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => {
                  onPick(s.data);
                  setOpen(false);
                }}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
              >
                <MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                <span>{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
